/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import { BackstageTheme } from '@backstage/theme';
import Box from '@material-ui/core/Box';
import { createStyles, makeStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { ComponentProps } from 'react';
import { IconComponent, useApp } from '@backstage/core-plugin-api';
import LanguageOutlinedIcon from '@material-ui/icons/LanguageOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import LibraryBooksOutlinedIcon from '@material-ui/icons/LibraryBooksOutlined';
import { Tooltip } from '@material-ui/core';

/** @public */
export type ItemCardHeaderClassKey = 'root';
type IconComponentProps = ComponentProps<IconComponent>;

const styles = (theme: BackstageTheme) =>
  createStyles({
    root: {
      color: theme.palette.common.white,
      backgroundImage: theme.getPageTheme({ themeId: 'card' }).backgroundImage,
      backgroundPosition: 0,
      backgroundSize: 'inherit',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      display: '-webkit-box',
    },
    subtitle: {
      fontSize: '12px',
      marginLeft: theme.spacing(1),
    },
    title: {
      padding: '0px',
      overflow: 'hidden',
      position: 'relative',
      display: 'inline-block',
      margin: '0 5px 0 5px',
      textDecoration: 'none',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  });

const useStyles = makeStyles(styles, { name: 'ItemCardHeader' });

/** @public */
export type ItemCardHeaderProps = Partial<
  WithStyles<Partial<typeof styles>>
> & {
  /**
   * A large title to show in the header, providing the main heading.
   *
   * Use this if you want to have the default styling and placement of a title.
   */
  title?: React.ReactNode;
  /**
   * A slightly smaller title to show in the header, providing additional
   * details.
   *
   * Use this if you want to have the default styling and placement of a
   * subtitle.
   */
  subtitle?: React.ReactNode;
  /**
   * Custom children to draw in the header.
   *
   * If the title and/or subtitle were specified, the children are drawn below
   * those.
   */
  children?: React.ReactNode;
  componentType?: string;
};

/**
 * A simple card header, rendering a default look for "item cards" - cards that
 * are arranged in a grid for users to select among several options.
 *
 * @remarks
 * This component expects to be placed within a Material UI `<CardMedia>`.
 *
 * Styles for the header can be overridden using the `classes` prop, e.g.:
 *
 * `<ItemCardHeader title="Hello" classes={{ root: myClassName }} />`
 *
 * @public
 */
export function ItemCardHeader(props: ItemCardHeaderProps) {
  const { title, subtitle, children, componentType = '' } = props;
  const classes = useStyles(props);
  const app = useApp();
  const getIcons = (iconProps: IconComponentProps) => {
    const defaultIcon = app.getSystemIcon('kind:component');

    const iconsObj: { [key: string]: IconComponent } = {
      website: LanguageOutlinedIcon,
      documentation: DescriptionOutlinedIcon,
      library: LibraryBooksOutlinedIcon,
    };

    const Icon = iconsObj[componentType] || defaultIcon;

    return <Icon {...iconProps} />;
  };

  return (
    <Box className={classes.root}>
      <Box flexDirection="row" display="flex" alignItems="center" width="100%">
        {componentType && <Box margin="0px 5px">{getIcons({})}</Box>}
        {title && (
          <Tooltip
            title={title}
            placement="bottom"
            PopperProps={{ style: { marginTop: -12 } }}
          >
            <Typography variant="h5" component="h2" className={classes.title}>
              {title}
            </Typography>
          </Tooltip>
        )}
        {subtitle && (
          <Typography
            variant="subtitle2"
            color="textSecondary"
            className={classes.subtitle}
            style={{ textTransform: 'capitalize' }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}
